// ============================================================================
// SINXRONLASH (Q-018)
//
// Har bir amal avval online sinab ko'riladi (PIN sessiyasi bilan). Internet
// bo'lmasa yoki javob kelmasa — lokal navbatga (outbox) yoziladi va keyin
// "offline_sync" sifatida, qilgan xodim nomidan yuboriladi. Navbat tartib
// bilan ketadi: smena ochilishi undagi buyurtmalardan oldin yetib boradi.
//
// Server xatosi (huquq, noto'g'ri ma'lumot) qayta urinish bilan tuzalmaydi —
// bunday amal "yuborilmadi" ro'yxatiga tushadi va kassada ko'rinadi.
// ============================================================================

import { ApiError } from '@onepos/client';
import { deviceApi, withTimeout } from './device';
import { idb, meta } from './idb';
import type { LocalOrder, OrderJson, ShiftInfo } from './logic';

export type OutboxKind =
  | 'order_create'
  | 'order_add_items'
  | 'order_pay'
  | 'order_status'
  | 'items_status'
  | 'order_void'
  | 'order_bill'
  | 'refund'
  | 'shift_open'
  | 'shift_close'
  | 'cash_move'
  | 'expense_add'
  | 'meal_give';

const RPC: Record<OutboxKind, string> = {
  order_create: 'pos_order_create',
  order_add_items: 'pos_order_add_items',
  order_pay: 'pos_order_pay',
  order_status: 'pos_order_status',
  items_status: 'pos_items_status',
  order_void: 'pos_order_void',
  order_bill: 'pos_order_bill',
  refund: 'pos_refund',
  shift_open: 'pos_shift_open',
  shift_close: 'pos_shift_close',
  cash_move: 'pos_cash_move',
  expense_add: 'pos_expense_add',
  meal_give: 'pos_meal_give',
};

export const KIND_LABEL: Record<OutboxKind, string> = {
  order_create: 'Buyurtma',
  order_add_items: 'Taom qo‘shish',
  order_pay: 'To‘lov',
  order_status: 'Holat',
  items_status: 'Oshxona holati',
  order_void: 'Bekor qilish',
  order_bill: 'Hisob cheki',
  refund: 'Pul qaytarish',
  shift_open: 'Smena ochish',
  shift_close: 'Smena yopish',
  cash_move: 'Kassa harakati',
  expense_add: 'Xarajat',
  meal_give: 'Xodim ovqati',
};

export interface OutboxItem {
  id?: number;
  kind: OutboxKind;
  client_id: string;
  payload: Record<string, unknown>;
  label: string;
  created_at: string;
  state: 'pending' | 'dead';
  attempts: number;
  error?: string;
}

export interface Actor {
  staffId: string;
  session: string | null;
}

/** Tarmoq bilan bog'liq (qayta urinsa o'tadi) */
export function isNetwork(e: unknown): boolean {
  return e instanceof ApiError && e.code === 'NETWORK';
}

/** Qurilma uzilgan yoki hisobi bekor bo'lgan */
export function isDeviceLost(e: unknown): boolean {
  return e instanceof ApiError && ['TERMINAL_NOT_REGISTERED', 'SESSION_EXPIRED', 'UNAUTHORIZED'].includes(e.code);
}

// ---------------------------------------------------------------------------
// Lokal buyurtmalar
// ---------------------------------------------------------------------------
export async function saveServerOrder(o: OrderJson): Promise<LocalOrder> {
  const prev = await idb.get<LocalOrder>('orders', o.client_id);
  const next: LocalOrder = { ...o, local_no: prev?.local_no, sync: 'synced' };
  await idb.put('orders', next);
  return next;
}

export async function localOrders(): Promise<LocalOrder[]> {
  return idb.all<LocalOrder>('orders');
}

export async function nextLocalNo(date: string): Promise<number> {
  const key = `local_no:${date}`;
  const n = ((await meta.get<number>(key)) ?? 0) + 1;
  await meta.set(key, n);
  return n;
}

/** Eski (3 kundan oldingi, yuborilgan) buyurtmalarni tozalash */
export async function pruneOrders(today: string): Promise<void> {
  const limit = new Date(`${today}T00:00:00Z`).getTime() - 3 * 86400_000;
  for (const o of await localOrders()) {
    if (o.sync === 'synced' && new Date(`${o.business_date}T00:00:00Z`).getTime() < limit) {
      await idb.del('orders', o.client_id);
    }
  }
}

// ---------------------------------------------------------------------------
// Amal: avval online, bo'lmasa navbat
// ---------------------------------------------------------------------------
export interface PerformResult<T> {
  result: T | null;
  queued: boolean;
}

export async function perform<T>(
  kind: OutboxKind,
  payload: Record<string, unknown>,
  actor: Actor,
  label: string,
): Promise<PerformResult<T>> {
  const body = { ...payload, created_at_local: new Date().toISOString() };
  // Navbatda eski amallar bo'lsa — avval ular (tartib buzilmasin: smena, keyin buyurtma)
  let queueBusy = await hasPending();
  if (queueBusy && actor.session && navigator.onLine) {
    await engine.flush();
    queueBusy = await hasPending();
  }
  if (actor.session && navigator.onLine && !queueBusy) {
    try {
      const result = await withTimeout(
        deviceApi.rpc<T>(RPC[kind], { p_session: actor.session, p_payload: { ...body, source: 'online' } }),
        12_000,
      );
      engine.markOnline(true);
      return { result, queued: false };
    } catch (e) {
      if (!isNetwork(e)) throw e;
      engine.markOnline(false);
    }
  }
  await enqueue(kind, { ...body, source: 'offline_sync', staff_id: actor.staffId }, label);
  return { result: null, queued: true };
}

async function hasPending(): Promise<boolean> {
  return (await outboxItems()).some((i) => i.state === 'pending');
}

export async function enqueue(kind: OutboxKind, payload: Record<string, unknown>, label: string): Promise<void> {
  const item: OutboxItem = {
    kind,
    client_id: String(payload.client_id ?? ''),
    payload,
    label,
    created_at: new Date().toISOString(),
    state: 'pending',
    attempts: 0,
  };
  await idb.put('outbox', item);
  engine.refresh();
  engine.kick(1500);
}

export async function outboxItems(): Promise<OutboxItem[]> {
  return (await idb.all<OutboxItem>('outbox')).sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
}

/** Yuborilmagan amalni qayta navbatga qo'yish */
export async function retryItem(id: number): Promise<void> {
  const item = await idb.get<OutboxItem>('outbox', id);
  if (!item) return;
  await idb.put('outbox', { ...item, state: 'pending', error: undefined });
  engine.refresh();
  engine.kick(0);
}

/** Yuborilmagan amalni butunlay o'chirish (ega/menejer qaroriga ko'ra) */
export async function discardItem(id: number): Promise<void> {
  const item = await idb.get<OutboxItem>('outbox', id);
  await idb.del('outbox', id);
  if (item?.kind === 'order_create') {
    const o = await idb.get<LocalOrder>('orders', item.client_id);
    if (o && o.sync !== 'synced') await idb.del('orders', item.client_id);
  }
  engine.refresh();
}

// ---------------------------------------------------------------------------
// Navbatni yuborish
// ---------------------------------------------------------------------------
export interface SyncStatus {
  online: boolean;
  pending: number;
  dead: number;
  syncing: boolean;
  deviceLost: boolean;
  lastSync: string | null;
}

type Listener = (s: SyncStatus) => void;
type Applied = (kind: OutboxKind, result: unknown) => void;

class SyncEngine {
  private status: SyncStatus = {
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
    pending: 0,
    dead: 0,
    syncing: false,
    deviceLost: false,
    lastSync: null,
  };
  private listeners = new Set<Listener>();
  private appliedListeners = new Set<Applied>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private interval: ReturnType<typeof setInterval> | null = null;
  private pushing: Promise<void> | null = null;

  get current(): SyncStatus {
    return this.status;
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    l(this.status);
    return () => {
      this.listeners.delete(l);
    };
  }

  /** Navbatdagi amal serverga yetgach (buyurtma raqami, smena ID) */
  onApplied(l: Applied): () => void {
    this.appliedListeners.add(l);
    return () => {
      this.appliedListeners.delete(l);
    };
  }

  private set(patch: Partial<SyncStatus>) {
    this.status = { ...this.status, ...patch };
    for (const l of this.listeners) l(this.status);
  }

  markOnline(online: boolean) {
    if (this.status.online !== online) this.set({ online });
  }

  start() {
    if (this.interval) return;
    const onOnline = () => {
      this.set({ online: true });
      this.kick(0);
    };
    const onOffline = () => this.set({ online: false });
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    this.interval = setInterval(() => this.kick(0), 20_000);
    void this.refresh();
    this.kick(500);
  }

  async refresh() {
    try {
      const items = await outboxItems();
      this.set({
        pending: items.filter((i) => i.state === 'pending').length,
        dead: items.filter((i) => i.state === 'dead').length,
      });
    } catch {
      /* IndexedDB yo'q (masalan, maxfiy rejim) */
    }
  }

  kick(delay: number) {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, delay);
  }

  /** Navbatni hozir yuborish (tugashini kutadi) */
  flush(): Promise<void> {
    if (!this.pushing) {
      this.pushing = this.push().finally(() => {
        this.pushing = null;
      });
    }
    return this.pushing;
  }

  setDeviceLost(lost: boolean) {
    this.set({ deviceLost: lost });
  }

  private async push() {
    if (this.status.deviceLost) return;
    if (!navigator.onLine) {
      this.set({ online: false });
      return;
    }
    this.set({ syncing: true });
    try {
      const items = (await outboxItems()).filter((i) => i.state === 'pending');
      for (const item of items) {
        try {
          const result = await withTimeout(
            deviceApi.rpc<unknown>(RPC[item.kind], { p_session: null, p_payload: item.payload }),
            20_000,
          );
          await this.apply(item, result);
          if (item.id !== undefined) await idb.del('outbox', item.id);
          this.set({ online: true, lastSync: new Date().toISOString() });
        } catch (e) {
          if (isNetwork(e)) {
            this.set({ online: false });
            break;
          }
          if (isDeviceLost(e)) {
            this.set({ deviceLost: true });
            break;
          }
          const msg = e instanceof Error ? e.message : String(e);
          await idb.put('outbox', { ...item, state: 'dead', attempts: item.attempts + 1, error: msg });
          if (item.kind === 'order_create') {
            const o = await idb.get<LocalOrder>('orders', item.client_id);
            if (o) await idb.put('orders', { ...o, sync: 'dead', error: msg });
          }
        }
      }
    } finally {
      this.set({ syncing: false });
      await this.refresh();
    }
  }

  private async apply(item: OutboxItem, result: unknown) {
    if (item.kind.startsWith('order_') || item.kind === 'refund') {
      const o = result as OrderJson | null;
      if (o && typeof o === 'object' && 'client_id' in o) await saveServerOrder(o);
    }
    if (item.kind === 'shift_open' || item.kind === 'shift_close') {
      const s = result as ShiftInfo | null;
      const local = await meta.get<ShiftInfo>('shift');
      if (s && local && (local.client_id === s.client_id || local.id === s.id)) {
        await meta.set('shift', s.status === 'closed' ? null : { ...local, ...s });
      }
    }
    for (const l of this.appliedListeners) l(item.kind, result);
  }
}

export const engine = new SyncEngine();
